import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ChartDisplayElementModel } from '../..//models/chart-display-element.model';
import { SharedReportModel } from '../../models/shared-report.model';
import { SharedReportInviteDialogComponent } from '../SharedReportInviteDialog/shared-report-invite-dialog.component';
import { ReportModel } from '../../models/report.model';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { SharedReportService } from '../../services/shared-report.service';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { SharedReportSearchService } from '../../services/shared-report-search.service';
import { SharedReportApiErrorEnum } from '../../enums/shared-report-api-error.enum';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { allowedFileTypes, FileUploadModel } from '../../../Forms/models/file-upload.model';
import { FileUploadComponent } from '../../../Forms/components/FileUpload/file-upload.component';
import { Observable, of, from, Subscription } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';
import { MediaServiceAccessTypeEnum } from '../../../../api/mediaService/enums/media-service-access-type.enum';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { ProminenceLockerService } from '../../../Misc/services/prominence-locker.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { dateFormats } from '../../../../Core/Users/data/date-formats';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import {
    ManageContentLibraryDialogComponent
} from '../../../../Common/ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';

@Component({
    selector: 'agpr-shared-report-create-dialog',
    templateUrl: './shared-report-create-dialog.component.html',
    styleUrls: ['./shared-report-create-dialog.component.scss'],
    standalone: false
})
export class SharedReportCreateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() report: ReportModel;
    @Input() sharedReport: SharedReportModel;
    @Input() charts: BlockModel[];
    @Input() updateSourceData: boolean;
    @Input() userPreferenceTimezone: string;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    activeChart: BlockModel;
    coverImage: FileUploadModel[]; // Single file, but FileUploadComponent tracks an array
    nameEmpty: boolean = false;
    titleEmpty: boolean = false;
    duplicateName: boolean = false;
    saveLoading: boolean = false;
    prominenceStatus: ProminenceRecalculationJobStatus;
    prominenceStatuses = ProminenceRecalculationJobStatus;
    reportBlockTypeEnum = ReportBlockTypeEnum;

    dateFormatOptions: DropdownOptionModel[];

    protected subscriptions: Subscription[] = [];

    readonly imageMIMETypesRegex: string = allowedFileTypes.imageMIMETypesRegex;
    readonly imageMIMETypes: string = [
        ...allowedFileTypes.imageMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ].join(',');
    readonly buttonTypes = ButtonTypesEnum;


    @ViewChild('fileUpload') fileUpload: FileUploadComponent;

    constructor(
        public coreDialogService: CoreDialogService,
        private reportsPageBuilderService: ReportsPageBuilderService,
        private sharedReportsService: SharedReportService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private sharedReportSearchService: SharedReportSearchService,
        private prominenceLockerService: ProminenceLockerService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'SharedReportCreate');

        this.dateFormatOptions = dateFormats.map(format => new DropdownOptionModel(this.localizationService.transform(format.name), format.momentFormat));
    }

    reportTitleChanged(value: string) {
        this.titleEmpty = !value || value.trim().length === 0;
    }

    reportNameChanged(value: string) {
        this.nameEmpty = !value || value.trim().length === 0;
        this.duplicateName = this.sharedReportSearchService.isTitleDuplicate(this.sharedReport.name, this.sharedReport?.id);
    }

    createSharedReport() {
        this.titleEmpty = !this.sharedReport.name;
        if (this.titleEmpty) {
            return;
        }
        this.createOrUpdateReportStream('create').subscribe(() => {
            this.closeDialog();
        });
    }

    updateSharedReport() {
        this.titleEmpty = !this.sharedReport.name;
        if (this.titleEmpty) {
            return;
        }
        this.createOrUpdateReportStream('update').subscribe(() => {
            this.closeDialog();
        });
    }

    openContentLibrary(): void {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                selectionMode: true,
                useAcceptedFilesTypes: true,
                singleSelect: true,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (selectedFiles && selectedFiles.length > 0) {
                        const fileUpload: FileUploadModel = {
                            id: selectedFiles[0].id,
                            name: selectedFiles[0].name,
                            size: selectedFiles[0].size,
                            url: selectedFiles[0].url
                        };
                        this.coverImage = [fileUpload];
                    }
                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    /**
     * Moves a chart and chart selected option up the list
     *
     * @param index - the series to move
     */
    moveChartUp(index: number) {
        if (index === 0) {
            return;
        }
        this.charts = this.moveUp(this.charts, index);
    }

    /**
     * Moves a chart and chart selected option down the list
     *
     * @param index - the series to move
     */
    moveChartDown(index: number): void {
        this.charts = this.moveDown(this.charts, index);
    }

    /**
     * Moves an item in an array down one position
     *
     * @param array - list to change order of
     * @param index - index of item to move down
     *
     * @returns any[] -  the array that was manipulated
     */
    moveDown(array: any[], index: number) {
        if (array && index === array.length - 1) {
            return;
        }
        const tempArray = array.slice();

        const itemToMoveDown = tempArray[index];
        const itemToMoveUp = tempArray[index + 1];

        tempArray.splice(index + 1, 1, itemToMoveDown);
        tempArray.splice(index, 1, itemToMoveUp);

        return tempArray;
    }

    /**
     * Moves an item in an array up one position
     *
     * @param array - list to change order of
     * @param index - index of item to move up
     *
     * @returns any[] -  the array that was manipulated
     */
    moveUp(array: any[], index: number): any[] {
        if (!array || index === 0) {
            return;
        }

        const tempArray = array.slice();

        const itemToMoveUp = tempArray[index];
        const itemToMoveDown = tempArray[index - 1];

        tempArray.splice(index, 1, itemToMoveDown);
        tempArray.splice(index - 1, 1, itemToMoveUp);

        return tempArray;
    }

    setChartIndex(): void {
        for (const [chartIndex, chart] of this.charts.entries()) {
            (chart.displayElement as ChartDisplayElementModel).index = chartIndex;
        }
    }

    setActiveChart(chart: BlockModel): void {
        this.activeChart = chart;
    }

    enableEditingNote(chartSelected: boolean, chart: BlockModel): void {
        if (chartSelected) {
            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = true;
        } else {
            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = false;
        }
    }

    cancel(): void {
        this.closeDialog();
    }

    ngOnInit(): void {
        if (!this.sharedReport) {
            this.sharedReport = new SharedReportModel();
            if (this.report.title) {
                this.sharedReport.name = this.report.title;
            }
            this.sharedReport.reportId = this.report.id;
            this.mapReportBlocksToCharts();
            this.sharedReport.charts = this.charts.slice();
        }

        if (this.updateSourceData) {
            this.mapReportBlocksToCharts();
            this.sharedReport.charts = this.charts.slice();
        }

        if (this.sharedReport.coverPageImageUrl) {
            this.coverImage = [
                {
                    id: this.sharedReport.coverPageImageUrl,
                    name: this.localizationService.transform('Discover.fileUpload.existing.image'),
                    size: 0,
                    type: 'N/A'
                },
            ];
        }
        this.setChartIndex();
        // enable editing note by default
        for (const chart of this.charts) {
            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = true;
        }

        this.subscriptions.push(
            this.prominenceLockerService.prominenceStatus$.subscribe(status => {
                this.prominenceStatus = status;
                if (this.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
                    // if prominence calculation is not completed, we do not allow users to create shared report with prominence charts
                    this.sharedReport.charts = this.sharedReport.charts.filter(chart => {
                        if (chart.type.key === ReportBlockTypeEnum.PROMINENCE_OVER_TIME || chart.type.key === ReportBlockTypeEnum.PROMINENCE_BY_REACH) {
                            (chart.displayElement as ChartDisplayElementModel).enableEditingNote = false;
                            return false;
                        } else {
                            return true;
                        }
                    });
                }
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                if (!this.sharedReport.dateFormat) {
                    this.sharedReport.dateFormat = dateFormats.find(format => format.id === prefs.dateFormatId).momentFormat;
                }
            })
        );
    }

    mapReportBlocksToCharts() {
        this.charts = this.report.blocks.map((block, i) => {
            const originalBlock = this.reportsPageBuilderService.getChartComponentFromBlock(block);
            const originalSeries = (block.displayElement as ChartDisplayElementModel).series;
            const chart = block.clone();
            chart.id = undefined;
            const chartElement = chart.displayElement as ChartDisplayElementModel;
            const dateRange = chartElement.series[0].dateRange;

            if (dateRange.type === DateQueryTypeEnum.ALL) {
                chartElement.styles.allDateRange = true;
            } else {
                chartElement.styles.absoluteDateRange = DateQueryModel.makeAbsoluteRange(chartElement.series[0].dateRange, this.userPreferenceTimezone);
            }

            chartElement.styles.chartOptions = originalBlock.chartOptions;

            if (originalBlock?.chart) {
                chartElement.styles.chartOptions = originalBlock.chart.options;
                chartElement.styles.chartMetadata = originalBlock.chart.metadata;
                if ((originalBlock.chart as any).chartSeries) {
                    chartElement.styles.chartSeries = (originalBlock.chart as any).chartSeries;
                }
            }

            chartElement.displayAnnotationInExport = (this.charts[i]?.displayElement as ChartDisplayElementModel)?.displayAnnotationInExport;

            if (chart.type.key === ReportBlockTypeEnum.COVERAGE_SUMMARY) {
                chartElement.styles.dateRangeLabelText = chartElement.dateRangeLabel;
            }

            chartElement.replaceAllSeries(originalSeries.map(series => {
                const newSeries = series.clone();
                newSeries.id = undefined;
                newSeries.dataProvider = series.dataProvider;
                newSeries.mentionProvider = series.mentionProvider;
                if (newSeries.subSeries?.length > 0) {
                    newSeries.styles.subSeries = newSeries.subSeries;
                }
                return newSeries;
            }));
            chartElement.originalReportBlockId = block.id;
            return chart;
        });
    }

    /**
     * Both creating and updating the report involve the same steps, so they can share the same stream
     */
    private createOrUpdateReportStream(actionType: ('create' | 'update')): Observable<number> {
        const isCreate = actionType === 'create';

        // Update the order from the charts, but only for the items that were selected
        this.sharedReport.charts = this.charts.filter(chart => this.sharedReport.charts.includes(chart));

        this.saveLoading = true;
        return this.uploadImageStream$().pipe(
            mergeMap(() => {
                if (isCreate) {
                    return this.sharedReportsService.createSharedReport(this.sharedReport).pipe(
                        tap(id => this.sharedReport.id = id)
                    );
                } else {
                    return this.sharedReportsService.updateSharedReport(this.sharedReport);
                }
            }),
            tap(id => {
                this.coreDialogService.open(SharedReportInviteDialogComponent, {
                    inputs: {
                        sharedReport: this.sharedReport,
                        canSkip: true
                    }
                });
                this.saveLoading = false;
                this.sharedReportSearchService.loadListAndSearch();
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(`Reports.sharedReportSetupDialog.${actionType}.success.heading`),
                    detail: this.localizationService.transform(`Reports.sharedReportSetupDialog.${actionType}.success.message`, {
                        name: this.sharedReport.name
                    })
                });
            }),
            catchError(err => {
                let errorDetailMessage = `Reports.sharedReportSetupDialog.${actionType}.error.message`;
                if (err?.error?.details && err.error.details[0]?.code === SharedReportApiErrorEnum.titleDuplicate) {
                    this.duplicateName = true;
                    errorDetailMessage = 'Reports.sharedReportSetupDialog.create.error.duplicateName';
                    this.sharedReportSearchService.loadListAndSearch();
                }

                this.saveLoading = false;
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform(`Reports.sharedReportSetupDialog.${actionType}.error.heading`),
                    detail: this.localizationService.transform( errorDetailMessage, {
                        name: this.sharedReport.name
                    })
                });

                return of(null);
            })
        );
    }

    /**
     * A stream that uploads the cover image file if it exists
     * @returns void representing completion of uploading
     */
    private uploadImageStream$(): Observable<void> {
        const coverImageName = this.coverImage?.[0]?.name;
        if (coverImageName === this.localizationService.transform('Discover.fileUpload.existing.image')) {
            return of(null);
        } else {
            return from(this.fileUpload.uploadFiles(MediaServiceAccessTypeEnum.PUBLIC)).pipe(
                map(([coverImageFile]) => {
                    // This value can be undefined, which represents either:
                    //      an empty field or
                    //      a removal of an existing one
                    this.sharedReport.coverPageImageUrl = coverImageFile?.url;
                })
            );
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
